import { ReactNode, useEffect, useRef } from 'react';

import background from './assets/background.png';
import backgroundMobile from './assets/background-mobile.png';
import left1 from './assets/left-1.png';
import footer from './assets/footer.png';
import footerMobile from './assets/footer-mobile.png';
import arc1 from './assets/arc-1.png';
import arc2 from './assets/arc-2.png';
import arc3 from './assets/arc-3.png';
import arc4 from './assets/arc-4.png';
import arc5 from './assets/arc-5.png';
import arc6 from './assets/arc-6.png';

import { Box, Stack } from '@mui/material';
import { useBreakpoints } from 'modules/common/hooks/useBreakpoints';

function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: any[]) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

export interface ILayoutProps {
  children?: ReactNode;
  headerSlot?: ReactNode;
  footerSlot?: ReactNode;
  className?: string;
}

const STICKY_HEIGHT = 185;

export function Layout({
  children,
  headerSlot,
  footerSlot,
}: ILayoutProps): JSX.Element {
  const { isMdDown } = useBreakpoints();
  const parallaxStarsRef = useRef<HTMLDivElement>(null);

  const parallaxBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMdDown) return;

    let lastKnownScrollPosition = 0;
    let ticking = false;

    const handleScroll = () => {
      lastKnownScrollPosition = window.scrollY;
      const element = document.getElementById('root');
      const height = element?.getBoundingClientRect().height;

      const shouldScrollParallax =
        height && height - lastKnownScrollPosition > window.innerHeight;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (parallaxStarsRef.current && shouldScrollParallax) {
            parallaxStarsRef.current.style.transform = `translateY(${lastKnownScrollPosition * 0.5}px)`;
          }

          if (parallaxBottomRef.current) {
            parallaxBottomRef.current.style.transform = `translateX(-${lastKnownScrollPosition * 0.3}px)`;
          }

          ticking = false;
        });

        ticking = true;
      }
    };

    const debouncedScrollHandler = debounce(handleScroll, 3);

    window.addEventListener('scroll', debouncedScrollHandler);

    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [isMdDown]);

  return (
    <>
      <Stack
        sx={{
          minHeight: '100vh',
          minWidth: 360,
          position: 'relative',
          overflow: 'clip',
          background: `linear-gradient(
          45deg, 
          #131315 0%, 
          rgba(27, 28, 29, 0.6) 25%,
          rgba(27, 28, 29, 0.6) 50%, 
          rgba(32, 33, 47, 1) 70%,
          
          rgba(255, 203, 103, 0.2) 90%, 
          rgba(237, 84, 9, 0.3) 100%
        )`,
        }}
      >
        <Box
          ref={parallaxStarsRef}
          sx={{
            position: 'absolute',
            left: 0,
            width: '100%',
            height: `calc(100% - ${STICKY_HEIGHT}px)`,
            maxHeight: '100%',

            background: `url('${isMdDown ? backgroundMobile : background}') no-repeat center/cover`,
            zIndex: -1,
            willChange: 'transform',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 125,
            display: { xs: 'none', md: 'block' },
            left: 0,
            width: isMdDown ? 80 : 303,
            height: 500,
            background: `url('${left1}') no-repeat center/cover`,
            willChange: 'transform',
            backgroundSize: 'contain',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 640 + STICKY_HEIGHT,
            right: isMdDown ? -55 : 55,
            width: isMdDown ? 120 : 80,
            height: 80,
            background: `url('${arc6}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 980 + STICKY_HEIGHT,
            left: isMdDown ? 0 : 55,
            width: isMdDown ? 120 : 256,
            height: 256,
            background: `url('${arc1}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 1440,
            right: isMdDown ? 0 : 160,
            width: isMdDown ? 120 : 160,
            height: 160,
            background: `url('${arc5}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 1740,
            left: isMdDown ? 0 : 55,
            width: isMdDown ? 120 : 256,
            height: 256,
            background: `url('${arc4}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 1940,
            right: isMdDown ? 0 : 180,
            width: isMdDown ? 120 : 160,
            height: 160,
            background: `url('${arc3}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 2200,
            left: 0,
            width: isMdDown ? 120 : 160,
            height: 160,
            background: `url('${arc2}') no-repeat center/cover`,
            zIndex: 0,
            willChange: 'transform',
            backgroundSize: 'contain',
          }}
        />

        {headerSlot}

        <Stack
          component="main"
          sx={{
            flexGrow: 1,
            zIndex: 1,
            pb: {
              xs: 2,
              md: 12,
            },
          }}
        >
          {children}
        </Stack>

        {footerSlot}
        {isMdDown && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 380,
              height: 390,
              background: `url('${footerMobile}') no-repeat center/contain`,
              zIndex: 0,
            }}
          />
        )}
        {!isMdDown && (
          <Box
            ref={parallaxBottomRef}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: isMdDown ? 520 : 966,
              height: isMdDown ? 235 : 439,
              background: `url('${footer}') no-repeat center/cover`,
              zIndex: 0,
              willChange: 'transform',
              backgroundSize: 'contain',
            }}
          />
        )}
      </Stack>
    </>
  );
}
